import React, { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { getAlreadyExistsError, getBooleanField, getTextField, useAsyncState } from '@lender/utils';
import { Button, LinearProgress } from '@mui/material';
import { FORMS_ROUTING_PARAMS, FormsPaths } from 'app/configs/routesConfig/forms';
import UploadDocumentsModal from 'app/shared/components/document/UploadDocumentsModal';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import useEditorLocationDescriptor from 'app/shared/components/forms/FormEditor/hooks/useEditorLocationDescriptor';
import {
  AutocompleteCDS,
  CheckboxCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import withPreparedData from 'hocs/withPreparedData';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { object } from 'yup';

import { FormViewerNavigationType } from 'constants/forms';
import {
  disclosureTypeOptions,
  formApplicationTypeOptions,
  formPurposeTypeOptions,
} from 'constants/options';
import useForm from 'hooks/useForm';
import useLoanId from 'hooks/useLoanId';
import { checkIsFieldEqualToValueInData } from 'utils/data';
import { getFormPurposeOptions, getFormPurposeSelectLabel } from 'utils/forms';
import { jsonStringify } from 'utils/json';
import { removeExcessiveSpaces } from 'utils/string';

import { createFormAction, createFormPdfAction } from 'store/forms/actions';
import { selectFormNavigationType, selectFormsListByState } from 'store/forms/selectors';
import { closeDialogAction } from 'store/global/actions';
import { getDocumentTypesAction } from 'store/system/actions';
import { selectDocumentTypesOptions } from 'store/system/selectors';

import { CreateFormModalTestIds } from './CreateFormModal.testIds';
import { formsApiConvertPOSTData } from './CreateFormModal.utils';

const schema = object().shape({
  name: getTextField().test('alreadyExist', getAlreadyExistsError('Form'), function () {
    return this.options.context?.isNameValid;
  }),
  documentType: getTextField(),
  formApplicationType: getTextField(),
  state: getTextField(),
  isUploadPdf: getBooleanField({ isRequired: false }),
  withPageNumbers: getBooleanField({ isRequired: false }),
});

const defaultDocumentTypeOptions = [];

const CreateFormModal = ({
  formAreaCode,
  documentTypeOptions = defaultDocumentTypeOptions,
  abortController,
  formRef = null,
  isPdfOnly = false,
  isLoanSpecificForm = false,
  onFinishFormUploading,
  defaultDocumentTypeName,
  applicationId,
}) => {
  const { push } = useHistory();
  const dispatch = useDispatch();
  const loanId = useLoanId();
  const editorLocationDescriptor = useEditorLocationDescriptor();

  const [isFormSubmitting, setIsFormSubmitting] = useAsyncState(false);

  const navigationType = useSelector(selectFormNavigationType);
  const stateForms = useSelector(selectFormsListByState(formAreaCode));

  const defaultDocumentOption = documentTypeOptions.find(
    document => document.name === defaultDocumentTypeName,
  );

  const {
    form,
    isFormChanged,
    setSchema,
    validationErrors,
    handleChange,
    setInForm,
    setMultipleInForm,
    validateField,
    validateForm,
  } = useForm({
    state: formAreaCode,
    name: '',
    documentType: defaultDocumentOption || documentTypeOptions[0],
    formApplicationType: formApplicationTypeOptions[0],
    isRequired: false,
    isUploadPdf: isPdfOnly,
    withPageNumbers: false,
    disclosureType: null,
    purposeType: null,
    formPurpose: null,
  });

  const purposeTypeValue = form.purposeType?.value ?? null;
  const formPurposeOptions = getFormPurposeOptions(purposeTypeValue);
  const formPurposeLabel = getFormPurposeSelectLabel(purposeTypeValue);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  const onNavigateToEditForm = useCallback(
    ({ formId, state, formType }) => {
      FormsPaths.editForm.navigateWith(
        push,
        {
          [FORMS_ROUTING_PARAMS.formId]: formId,
          [FORMS_ROUTING_PARAMS.area]: state,
          [FORMS_ROUTING_PARAMS.formType]: formType,
          [FORMS_ROUTING_PARAMS.selectedPage]: 1,
        },
        editorLocationDescriptor,
      );
    },
    [push, editorLocationDescriptor],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      const name = removeExcessiveSpaces(form.name);
      const isNameValid = !checkIsFieldEqualToValueInData({
        data: stateForms,
        value: name,
        fieldName: 'name',
      });

      if (!(await validateForm({ isNameValid, noFormChangedValidate: true }))) {
        return;
      }

      setIsFormSubmitting(true);

      const dataToSend = formsApiConvertPOSTData(form);

      dispatch(createFormAction(dataToSend))
        .then(({ payload }) => {
          dispatch(closeDialogAction());
          onNavigateToEditForm(payload);
        })
        .catch(Error)
        .finally(() => setIsFormSubmitting(false));
    },
    [form, stateForms, validateForm, setIsFormSubmitting, dispatch, onNavigateToEditForm],
  );

  const onNameChange = useCallback(
    e => {
      const name = removeExcessiveSpaces(e.target.value);
      const isNameValid = !checkIsFieldEqualToValueInData({
        data: stateForms,
        value: name,
        fieldName: 'name',
      });
      handleChange(e, { isNameValid });
    },
    [handleChange, stateForms],
  );

  const uploadFile = useCallback(
    async file => {
      const name = removeExcessiveSpaces(form.name);
      const isNameValid = !checkIsFieldEqualToValueInData({
        data: stateForms,
        value: name,
        fieldName: 'name',
      });

      if (!(await validateForm({ isNameValid, noFormChangedValidate: true }))) {
        return;
      }

      const fd = new FormData();

      fd.append('file', file, file.name);
      fd.append('body', jsonStringify(formsApiConvertPOSTData(form, applicationId)));

      if (isLoanSpecificForm) {
        fd.set('loanId', loanId);
      }

      return dispatch(createFormPdfAction({ formData: fd, signal: abortController.signal }))
        .then(({ payload }) => {
          dispatch(closeDialogAction());

          if (navigationType === FormViewerNavigationType.URL) {
            onNavigateToEditForm(payload);
          }

          if (navigationType !== FormViewerNavigationType.URL) {
            onFinishFormUploading(payload);
          }
        })
        .catch(Error);
    },
    [
      form,
      stateForms,
      validateForm,
      isLoanSpecificForm,
      dispatch,
      abortController,
      applicationId,
      loanId,
      navigationType,
      onNavigateToEditForm,
      onFinishFormUploading,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const onPurposeTypeChange = useCallback(
    (e, value) => {
      setMultipleInForm({ purposeType: value || null, formPurpose: null });
    },
    [setMultipleInForm],
  );

  return (
    <div className="w-full">
      <LinearProgress className={isFormSubmitting ? 'visible' : 'invisible'} color="primary" />

      <form
        noValidate
        className="flex flex-col gap-12 min-w-400"
        data-ischanged={isFormChanged}
        ref={formRef}
        onSubmit={onSubmit}
      >
        <TextFieldCDS
          autoFocus
          fullWidth
          required
          data-testid={CreateFormModalTestIds.NAME_INPUT}
          disabled={isFormSubmitting}
          error={!!validationErrors.name}
          helperText={validationErrors.name}
          id="formName"
          label="Form Name"
          maxLength={150}
          name="name"
          value={form.name}
          variant="outlined"
          onChange={onNameChange}
        />

        <AutocompleteCDS
          required
          dataTestId={
            CreateFormModalTestIds.CREATE_FORM_MODAL_FORMS_CREATE_FORM_AUTOCOMPLETE_SELECT
          }
          error={!!validationErrors.documentType}
          helperText={validationErrors.documentType}
          label="Document Type"
          name="documentType"
          options={documentTypeOptions}
          value={form.documentType}
          onChange={onAutocompleteChange}
        />

        <AutocompleteCDS
          disableClearable
          dataTestId={CreateFormModalTestIds.FORM_APPLICATION_TYPE_SELECT}
          disabled={isFormSubmitting}
          error={!!validationErrors.formApplicationType}
          helperText={validationErrors.formApplicationType}
          label="Form Type"
          name="formApplicationType"
          options={formApplicationTypeOptions}
          value={form.formApplicationType}
          onChange={onAutocompleteChange}
        />

        <AutocompleteCDS
          dataTestId={CreateFormModalTestIds.DISCLOSURE_TYPE_SELECT}
          disabled={isFormSubmitting}
          label="Disclosure Type"
          name="disclosureType"
          options={disclosureTypeOptions}
          value={form.disclosureType}
          onChange={onAutocompleteChange}
        />

        <AutocompleteCDS
          dataTestId={CreateFormModalTestIds.PURPOSE_TYPE_SELECT}
          disabled={isFormSubmitting}
          label="Form Purpose"
          name="purposeType"
          options={formPurposeTypeOptions}
          value={form.purposeType}
          onChange={onPurposeTypeChange}
        />

        <AutocompleteCDS
          dataTestId={CreateFormModalTestIds.FORM_PURPOSE_SELECT}
          disabled={isFormSubmitting}
          isHidden={!purposeTypeValue}
          label={formPurposeLabel}
          name="formPurpose"
          options={formPurposeOptions}
          value={form.formPurpose}
          onChange={onAutocompleteChange}
        />

        <CheckboxCDS
          fullWidth
          data-testid={CreateFormModalTestIds.WITH_PAGE_NUMBERS_CHECKBOX}
          error={!!validationErrors.withPageNumbers}
          helperText={validationErrors.withPageNumbers}
          label="With Page Numbers"
          name="withPageNumbers"
          value={form.withPageNumbers}
          onChange={handleChange}
        />

        <CheckboxCDS
          fullWidth
          data-testid={CreateFormModalTestIds.UPLOAD_PDF_CHECKBOX}
          disabled={isFormSubmitting || isPdfOnly}
          error={!!validationErrors.isUploadPdf}
          helperText={validationErrors.isUploadPdf}
          label="Upload a PDF"
          name="isUploadPdf"
          value={form.isUploadPdf}
          onChange={handleChange}
        />

        {form.isUploadPdf && (
          <UploadDocumentsModal
            isUploadButtonHidden
            abortController={abortController}
            isModal={false}
            isMultiple={false}
            uploadFile={uploadFile}
          />
        )}

        {!form.isUploadPdf && (
          <div className="flex justify-end">
            <Button
              className="whitespace-nowrap ml-8"
              data-testid={CreateFormModalTestIds.SUBMIT_BUTTON}
              disabled={!isFormChanged || isFormSubmitting}
              type="submit"
              variant="contained"
            >
              Add
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

CreateFormModal.propTypes = {
  ...WithLoadingPropTypes,
  abortController: PropTypes.object.isRequired,
  formAreaCode: PropTypes.string.isRequired,
  applicationId: PropTypes.string,
  defaultDocumentTypeName: PropTypes.string,
  documentTypeOptions: PropTypes.array,
  formRef: PropTypes.shape({ current: PropTypes.object }),
  isLoanSpecificForm: PropTypes.bool,
  isPdfOnly: PropTypes.bool,
};

export default compose(
  withLoading,
  withPreparedData({
    fetchData: [getDocumentTypesAction],
    dataSelectors: { documentTypeOptions: selectDocumentTypesOptions },
    skeletonComponent: <LinearProgress color="primary" />,
  }),
  memo,
)(CreateFormModal);
